/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * HintComponent.tsx - Hint information component.
 */

import TenFrame from './TenFrame/TenFrame';
import VerticalTenFrame from './TenFrame/VerticalTenFrame';
import GroupTenFrame from './TenFrame/GroupTenFrame';
import Dice5TenFrame from './TenFrame/Dice5TenFrame';
import BaseTen from './BaseTen/BaseTen';
import BarModel from './BarModel/BarModel';
import Sequence from './Sequence/Sequence';
import DivisionArray from './Array/DivisionArray';
import HundredSquare from './HundredSquare/HundredSquare';
import TwoBaseTens from './TwoBaseTens/TwoBaseTens';
import TwoBaseTensSubtraction from './TwoBaseTens/TwoBaseTensSubtraction';
import { Hint, Format, Question, EquationQuestion } from '../types';
import { ADD, DIV, MULT, SUB } from '../../../mathSymbols';
import VerticalFrames from './VerticalFrames';
import BaseTenMultiplication from './BaseTen/BaseTenMultiplication';
import MultiplicationArray from './Array/MultiplicationArray';
import { useWindowDimensions } from 'react-native';

interface Props {
  question: Question,
}

/**
 * Component to render the hint information.
 * 
 * Switches on the given hint type, performs some calculations and returns the relevant hint component(s).
 */

const HINT_SCREEN_INSET = 0.9;
const HINT_VERTICAL_SPACE = 0.8;
const HINT_PADDING = 0.9;

const HintComponent = ({ question }: Props): JSX.Element => {

  const windowDimensions = useWindowDimensions();
  const useableHeight = Math.floor(windowDimensions.height * HINT_SCREEN_INSET * HINT_VERTICAL_SPACE * HINT_PADDING); 
  const usableWidth = Math.floor(windowDimensions.width * HINT_SCREEN_INSET * HINT_PADDING);

  switch (question.hint) {

    case Hint.ADDITION_TEN_FRAME: {
      if (question.sign !== ADD) { throw Error('TEN_FRAME for non-addition question'); }
      const { left, right } = equationOrder(question);
      const frames = fillTenFrames(left, right);

      return <>{
        frames.map((frame, index) => 
          <TenFrame numFrames={frames.length} numReds={frame.reds} numYellows={frame.yellows} usableWidth={usableWidth} usableHeight={useableHeight} key={index} />
        )
      }</>;
    }
    
    case Hint.ODD_EVEN_TEN_FRAME: {
      const frames = fillTenFrames(question.numA, 0);
      
      return <VerticalFrames key="multipleTenFrames">{
        frames.map((frame, index) => 
          <VerticalTenFrame numFrames={frames.length} numCounters={frame.reds} color="red" usableWidth={usableWidth} usableHeight={useableHeight} key={'tenframe' + index} />
        )
      }</VerticalFrames>;
    }

    case Hint.MULTIPLY_TWO_TEN_FRAME: {
      if (question.sign !== MULT) { throw Error('MULTIPLY_TWO_TEN_FRAME for non-multiplication question'); }
      const { result } = equationOrder(question);
      if (result % 2 !== 0) { throw Error('MULTIPLY_TWO_TEN_FRAME answer was not even'); }
      let multiplicand = result / 2;

      const frames: { reds: number, yellows: number }[] = [];

      while (multiplicand > 0) {
        // Take as many (pairs of) counters as we can.
        const pairsThisFrame = Math.min(multiplicand, 5);
        multiplicand -= pairsThisFrame;

        // Half (rounded down) are reds
        const redPairs = Math.floor(pairsThisFrame/2);
        // The rest are yellow
        const yellowPairs = pairsThisFrame - redPairs;
      
        frames.push({ reds: redPairs*2, yellows: yellowPairs*2 });
      }

      // Handle 0x2
      if (frames.length === 0) { frames.push({ reds: 0, yellows: 0 }); }

      return <VerticalFrames key="multipleTenFrames">{
        frames.map((frame, index) => <GroupTenFrame numFrames={frames.length} numReds={frame.reds} numYellows={frame.yellows} usableWidth={usableWidth} usableHeight={useableHeight} key={index} />)
      }</VerticalFrames>;
    }

    case Hint.FIVE_DICE_TEN_FRAME: {
      if (question.sign !== MULT) { throw Error('FIVE_DICE_TEN_FRAME for non-x5 question'); }
      const { result } = equationOrder(question);
      if (result % 5 !== 0) { throw Error('FIVE_DICE_TEN_FRAME answer was not divisible by 5'); }
      const multiplicand = result / 5;

      const frames = fillTenFrames(multiplicand, 0);  // Each die in the 10 frame represents 5

      return <>{
        frames.map((frame, index) => {
          return <Dice5TenFrame numFrames={frames.length} answer={frame.reds} usableWidth={usableWidth} usableHeight={useableHeight} key={'tenframe' + index} />;
        })
      }</>;
    }

    case Hint.DOUBLE_TEN_FRAMES: {
      return <>
        <TenFrame numFrames={2} numReds={question.numA} numYellows={0} usableWidth={usableWidth} usableHeight={useableHeight} key='Double1Frame' />
        <TenFrame numFrames={2} numReds={0} numYellows={question.numA} usableWidth={usableWidth} usableHeight={useableHeight} key='Double2Frame' />
      </>;
    }

    case Hint.MULTIPLE_TEN_FRAMES: {
      return <VerticalFrames key="multipleTenFrames">
        <VerticalTenFrame numFrames={2} numCounters={question.numA} color='red' usableWidth={usableWidth} usableHeight={useableHeight} key='Double1Frame' />
        <VerticalTenFrame numFrames={2} numCounters={question.numB} color='yellow' usableWidth={usableWidth} usableHeight={useableHeight} key='Double2Frame' />
      </VerticalFrames>;
    }

    case Hint.TEN_FRAME_SUBTRACT: {
      if (question.sign !== SUB) { throw Error('TEN_FRAME_SUBTRACT for non-subtraction question'); }
      const { subtrahend, difference } = equationOrderSubtraction(question);
      const frames = fillTenFrames(difference, subtrahend);

      return <>{
        frames.map((frame, index) => 
          <TenFrame numFrames={frames.length} numReds={frame.reds} numYellows={frame.yellows} usableWidth={usableWidth} usableHeight={useableHeight} isSubtract key={index} />
        )
      }</>; 
    }

    case Hint.BASE_TEN: {
      if (question.sign !== MULT) { throw Error('BASE_TEN for non-multiplication question'); }
      const total = equationOrder(question).result;

      const tens = Math.floor(total / 10);
      const ones = total - (tens * 10);

      return <BaseTen tens={tens} ones={ones} />;
    }

    case Hint.BASE_TEN_MULTIPLICATION: {
      if (question.sign !== MULT) { throw Error('BASE_TEN_MULTIPLICATION for non-multiplication question'); }
      const { left, right } = equationOrder(question);
      const multiplier = question.numA; // Always fix multiplier to numA
      const rows = left !== multiplier ? left : right; // The rows is the other factor
      return <BaseTenMultiplication multiplier={multiplier} rows={rows} usableWidth={usableWidth} key="Base_Ten_Multiplication" />;
    }

    case Hint.DIVIDE_BASE_TEN: {
      if (question.sign !== DIV) { throw Error('DIVIDE_BASE_TEN for non-division question'); }
      const total = equationOrder(question).result;
      return <BaseTen tens={total} ones={0} />;
    }

    case Hint.TWO_BASE_TENS: {
      const { left, right } = equationOrder(question);
      return <TwoBaseTens numA={left} numB={right} sign={question.sign} />;
    }

    case Hint.TWO_BASE_TENS_SUBTRACTION: {
      const { left, right } = equationOrder(question);
      return <TwoBaseTensSubtraction numA={left} numB={right} />;
    }

    case Hint.BAR_MODEL: {
      const [dividend, divisor] = (() => {
        if (question.format === Format.WordEquation) {
          // Assume action is half
          return [question.numA, 2];
        } else {
          const order = equationOrder(question);
          return [order.left, order.right];
        }
      })();

      return <BarModel topNumber={dividend} numBars={divisor} />;
    }

    case Hint.SEQUENCE: {
      return <Sequence step={question.sequence} answer={question.answer} format={question.format} />;
    }

    case Hint.DIVIDE_ARRAY: {
      if (question.sign !== DIV) { throw Error('DIVIDE_ARRAY for non-division question'); }
      const { divisor, quotient } = equationOrderDivision(question);
      return <DivisionArray numCols={divisor} numRows={quotient} />;
    }

    case Hint.MULTIPLICATION_ARRAY: {
      if (question.sign !== MULT) { throw Error('MULTIPLICATION_ARRAY for non-multiplication question'); }
      const { left, right } = equationOrder(question);
      return <MultiplicationArray numCols={right} numRows={left} />;
    }

    case Hint.HUNDRED_SQUARE: {
      const { left } = equationOrder(question);
      return <HundredSquare numA={left} usableWidth={usableWidth} usableHeight={useableHeight}/>;
    }
  }
};

export default HintComponent;

/**
 * Equation questions are all of the form `left @ right = result` where @ is some mathematical symbol. This function
 * extracts those numbers in the order they appear to the user (though the result may be on the left).
 * 
 * For example
 * - in addition: left=left addend, right=right addend, result=sum
 * - in multiplication: left=multiplicand, right=multiplier, result=product
 *   - though multiplicand and multiplier are often simply called left/right factors
 * - in subtraction: left=minuend, right=subtrahend, result=difference
 * - in division: left=dividend, right=divisor, result=quotient
 */
const equationOrder = (
  question: EquationQuestion
): { left: number; right: number; result: number; } => {
  switch (question.format) {
    case Format.Equation1:
    case Format.Equation2:
      return { left: question.numA, right: question.numB, result: question.answer };
    case Format.Equation3:
    case Format.Equation4:
      return { left: question.numB, right: question.numA, result: question.answer };
    case Format.Equation5:
    case Format.Equation7:
      return { left: question.numA, right: question.answer, result: question.numB };
    case Format.Equation6:
    case Format.Equation8:
      return { left: question.answer, right: question.numA, result: question.numB };
  }
};

/** Like {@link equationOrder} but with nicer terminology for subtraction. */
const equationOrderSubtraction = (
  question: EquationQuestion
): { minuend: number; subtrahend: number; difference: number; } => {
  if (question.sign !== SUB) { throw Error('Wrong sign for subtraction question'); }
  const order = equationOrder(question);
  return { minuend: order.left, subtrahend: order.right, difference: order.result};
};

/** Like {@link equationOrder} but with nicer terminology for division. */
const equationOrderDivision = (
  question: EquationQuestion
): { dividend: number; divisor: number; quotient: number; } => {
  if (question.sign !== DIV) { throw Error('Wrong sign for division question'); }
  const order = equationOrder(question);
  return { dividend: order.left, divisor: order.right, quotient: order.result};
};

/**
 * Distribute red and yellow counters in a set of ten frames.
 * @param reds - Number of red counters
 * @param yellows - Number of yellow counters
 * @returns Array of objects, each representing the number of red and yellow counters for that frame.
 */
const fillTenFrames = (
  reds: number,
  yellows: number
): { reds: number, yellows: number }[] => {
  const frames: { reds: number; yellows: number }[] = [];

  while (reds > 0 || yellows > 0) {
    // Take as many reds as we can, up to the space in the ten frame (i.e. 10).
    const redsThisFrame = Math.min(reds, 10);
    reds -= redsThisFrame;

    // Take as many yellows as we can, up to the remaining space in the ten frame.
    const yellowsThisFrame = Math.min(yellows, 10-redsThisFrame);
    yellows -= yellowsThisFrame;

    frames.push({ reds: redsThisFrame, yellows: yellowsThisFrame });
  }

  // If we didn't add any ten frames, add an empty one.
  if (frames.length === 0) { frames.push({ reds: 0, yellows: 0}); }

  return frames;
};
